import { useTheme } from "@emotion/react";
import {
  Business,
  Check,
  CheckCircle,
  Circle,
  Delete,
  Person,
  Star,
  StarBorder,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useState } from "react";
import useDelete from "../../../../../hooks/useDelete";
import usePut from "../../../../../hooks/usePut";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { buildFeedbackQueryKey } from "../hooks/useGetFeedbacks";
import { FEEDBACK, FEEDBACK_MARK } from "../../../../../enums/ApiPaths";

export default function FeedbackCard({ feedback, type, apiKey }) {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const key = buildFeedbackQueryKey(apiKey, searchParams);
  const { t } = useTranslation();
  const delUrl = FEEDBACK;
  const queryKey = "deleteing_rating";
  const confirmationMessage = "DeleteFeedbackConfirmation";
  const deleteFn = useDelete(
    feedback?.id,
    delUrl,
    queryKey,
    confirmationMessage,
    null,
    key
  );
  const readUrl = `${FEEDBACK_MARK}${feedback?.id}`;
  const isRead = feedback?.read;
  const post = usePut(readUrl, "updating_ratings", key);

  const [openDialog, setOpenDialog] = useState(false);

  const handleDelete = async () => {
    await deleteFn.deleteFn();
  };

  const handleMarkRead = async () => {
    await post.submitCallback();
  };

  return (
    <>
      <Card
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
          mb: 2,
          maxWidth: 550,
          boxShadow: theme.shadows[1],
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <TopInfo feedback={feedback} isRead={isRead} type={type} />
            <Box>
              <IconButton
                onClick={() => handleDelete()}
                color="error"
                aria-label="delete feedback"
                size="small"
                title={t("Delete")}
              >
                <Delete fontSize="small" />
              </IconButton>
              {!isRead ? (
                <IconButton
                  onClick={() => handleMarkRead()}
                  color="primary"
                  aria-label="delete feedback"
                  size="small"
                  title={t("MarkAsRead")}
                >
                  <Check fontSize="small" />
                </IconButton>
              ) : null}
            </Box>
          </Box>

          <RatingStars feedback={feedback} />
          <Description feedback={feedback} />

          {feedback.description?.length > 70 && (
            <Button
              size="small"
              onClick={() => setOpenDialog(true)}
              sx={{ mt: 1 }}
            >
              {t("More")}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Dialog for full description */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {t("Feedback")}: {feedback.userFullName}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">{feedback.description}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>{t("Close")}</Button>
        </DialogActions>
      </Dialog>

      <deleteFn.ConfirmationDialog />
    </>
  );
}

function RatingStars({ feedback }) {
  return (
    <Box display="flex" alignItems="center" mt={1.5} mb={1.5}>
      {[...Array(5)].map((_, i) =>
        i < feedback.rating ? (
          <Star key={i} color="primary" fontSize="small" />
        ) : (
          <StarBorder key={i} color="primary" fontSize="small" />
        )
      )}
      <Chip label={feedback.rating} size="small" sx={{ ml: 1 }} />
    </Box>
  );
}

function TopInfo({ feedback, isRead, type }) {
  const { t } = useTranslation();
  return (
    <Box display="flex" alignItems="center">
      <Avatar sx={{ width: 40, height: 40, mr: 2 }}>
        {type === "user" ? <Person /> : <Business />}
      </Avatar>
      <Box>
        <Typography variant="subtitle1" fontWeight="medium">
          {feedback.userFullName || t("DeletedUser")}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          textAlign={"center"}
        >
          {isRead ? (
            <Tooltip title={t("ReadPast")}>
              <CheckCircle color="success" sx={{ fontSize: 14, ml: 1 }} />
            </Tooltip>
          ) : (
            <Tooltip title={t("Unread")}>
              <Circle color="error" sx={{ fontSize: 14, ml: 1 }} />
            </Tooltip>
          )}
        </Typography>
      </Box>
    </Box>
  );
}

function Description({ feedback }) {
  return (
    <Typography
      variant="body2"
      sx={{
        mt: 1,
        display: "-webkit-box",
        WebkitLineClamp: 1,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {feedback.description}
    </Typography>
  );
}
